import { useEffect, useState } from "react"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import TextInput from "components/inputs/textInput"
import useCategories from "hooks/useCategories"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import { decodeFirebaseError } from "utils/firebaseHelpers"

interface DeleteCategoryFormProps {
    storageId: string,
    categoryId: string
}

const DeleteCategoryForm: React.FC<DeleteCategoryFormProps> = ({ storageId, categoryId }) => {

    const { t } = useTranslation()

    // Form data.
    const [name, setName] = useState(``)
    const [confirmation, setConfirmation] = useState(``)
    const [confirmed, setConfirmed] = useState(false)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    const {
        category,
        deleteCategoryAsync,
        isDeleting
    } = useCategories({ storageId, categoryId })

    const { closeDialog, addToast } = useLayout()

    useEffect(() => {
        if ( !category ) return
        setName(category.name)
    }, [category])

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (storageId === undefined || !confirmed) return
        
        try {
            await deleteCategoryAsync({ storageId, categoryId })
            addToast({ text: t('toastCategoryDeleted'), colorScheme: 'green' })
            closeDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    // Handle confirmation change.
    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === name)
    }

    // Catch missing ID or failed loading storage.
    if (!storageId) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>

    // Print the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isDeleting}
        >
            <Section>
                <p>{t('deleteCategoryConfirmation', { name })}</p>
                <TextInput label={t('typeCategoryNameToConfirm')} value={confirmation} onChange={handleConfirmationChange} placeholder={name} />
                <ButtonGroup>
                    <Button wideOnMobile type="button" design="optional" disabled={isDeleting} onClick={closeDialog}><CloseIcon /><span>{t('buttonCancel')}</span></Button>
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}><DeleteIcon /><span>{t('buttonDelete')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default DeleteCategoryForm



